/**
 * `Memory_Executor.ts` — recall of the user's own conversation history.
 *
 * Web-first; there is no Unity counterpart. Reads the signed-in user's own
 * transcript rows through the sparse user/time GSI. Content matching is local
 * after the bounded Dynamo query because the table is an event source, not a
 * vector index.
 */

import { AgentToolNames } from "@ai/registry/agentToolNames";
import { MEMORY_LOOKBACK_MONTHS } from "@ai/registry/Memory_Registry";
import type { IAITranscriptService } from "@ai/transcript/IAITranscriptService";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { getString, type IDomainExecutor, type ToolArgs } from "./AgentToolExecutorModel";

const K_MAX_RESULTS = 12;
const K_MAX_RESULT_TEXT_CHARS = 2_000;

export class MemoryExecutor implements IDomainExecutor {
  constructor(
    private readonly m_transcript: IAITranscriptService,
    private readonly m_auth: IAuthModel,
  ) {}

  async tryExecute(toolName: string, args: ToolArgs): Promise<string | null> {
    switch (toolName) {
      case AgentToolNames.RecallFromMemory:
        return this.recall(args);
      default:
        return null;
    }
  }

  private async recall(args: ToolArgs): Promise<string> {
    const user = this.m_auth.currentUser;
    if (!user?.customerId || !user.userId) return "error: memory_requires_authenticated_user";

    const matchTypeArg = getString(args, "matchType")?.toLowerCase() ?? "literal";
    if (matchTypeArg !== "literal" && matchTypeArg !== "similar") {
      return "error: matchType must be 'literal' or 'similar'";
    }

    const now = Date.now();
    const lookbackStart = subtractUtcMonths(now, MEMORY_LOOKBACK_MONTHS);
    const since = parseDateArg(getString(args, "since"), false);
    const until = parseDateArg(getString(args, "until"), true);
    if (since.kind === "invalid") return "error: since must be an ISO-8601 date (YYYY-MM-DD)";
    if (until.kind === "invalid") return "error: until must be an ISO-8601 date (YYYY-MM-DD)";

    const requestedSince = since.timestamp ?? lookbackStart;
    const requestedUntil = until.timestamp ?? now;
    const sinceTimestamp = Math.max(lookbackStart, requestedSince);
    const untilTimestamp = Math.min(now, requestedUntil);
    const lookbackClamped = requestedSince < lookbackStart;
    if (sinceTimestamp > untilTimestamp) {
      return `ok: No recallable history falls in that window. Memory starts ${isoDate(lookbackStart)}.`;
    }

    try {
      const result = await this.m_transcript.recall({
        customerId: user.customerId,
        userId: user.userId,
        query: getString(args, "query"),
        matchType: matchTypeArg,
        sinceTimestamp,
        untilTimestamp,
        maxResults: K_MAX_RESULTS,
      });
      if (result.exchanges.length === 0) {
        return [
          "ok: No matching questions or answers were found in the user's transcript.",
          `Window: ${isoDate(sinceTimestamp)} through ${isoDate(untilTimestamp)}.`,
          lookbackClamped ? `Memory is limited to the last ${MEMORY_LOOKBACK_MONTHS} months.` : "",
          result.readTruncated ? "The read safety limit was reached, so older matches may exist." : "",
        ].filter(Boolean).join(" ");
      }

      const partPath = getString(args, "partPath");
      return `ok: ${JSON.stringify({
        source: "spatial-transcript",
        window: {
          since: new Date(sinceTimestamp).toISOString(),
          until: new Date(untilTimestamp).toISOString(),
          lookbackClamped,
        },
        matchType: matchTypeArg,
        matches: result.exchanges.map((exchange) => {
          const question = clipResultText(exchange.question);
          const answer = exchange.answer === null ? null : clipResultText(exchange.answer);
          return {
            askedAt: new Date(exchange.askedAt).toISOString(),
            answeredAt:
              exchange.answeredAt === null ? null : new Date(exchange.answeredAt).toISOString(),
            question: question.text,
            answer: answer?.text ?? null,
            contentClipped: question.clipped || (answer?.clipped ?? false),
            projectId: exchange.projectId,
            workflowId: exchange.workflowId,
            stepId: exchange.stepId,
            stepName: exchange.stepName,
            sceneId: exchange.sceneId,
            inputType: exchange.inputType,
          };
        }),
        omittedMatchCount: result.omittedMatchCount,
        readTruncated: result.readTruncated,
        ...(partPath
          ? { note: "partPath was not applied because stored transcripts do not yet carry stable part identity." }
          : {}),
      })}`;
    } catch (error) {
      console.warn("[MemoryExecutor] Recall query failed:", error);
      return "error: memory_lookup_failed";
    }
  }
}

type ParsedDate = { kind: "valid"; timestamp: number | null } | { kind: "invalid" };

function parseDateArg(value: string | null, endOfDay: boolean): ParsedDate {
  if (value === null) return { kind: "valid", timestamp: null };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return { kind: "invalid" };
  const timestamp = Date.parse(`${value}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}Z`);
  if (!Number.isFinite(timestamp) || isoDate(timestamp) !== value) return { kind: "invalid" };
  return { kind: "valid", timestamp };
}

function subtractUtcMonths(timestamp: number, months: number): number {
  const date = new Date(timestamp);
  date.setUTCMonth(date.getUTCMonth() - months);
  return date.getTime();
}

function isoDate(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function clipResultText(text: string): { readonly text: string; readonly clipped: boolean } {
  if (text.length <= K_MAX_RESULT_TEXT_CHARS) return { text, clipped: false };
  return {
    text: `${text.slice(0, K_MAX_RESULT_TEXT_CHARS)}… [result clipped]`,
    clipped: true,
  };
}
